/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 图片处理 URL Hook（支持阿里云/Tencent 缩略与预览）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10
 */

import { useCallback } from 'react'
import CONFIG from '../../../../constants/config'

const MAX_DPR = 2
const MIN_THUMB_WIDTH = 200

const isTencentUrl = (baseUrl: string) => {
  return (
    baseUrl.includes(CONFIG.tencentPhotoPrefix) ||
    baseUrl.includes('net-cloud-tx.xhey.top') ||
    baseUrl.includes('/tencent-')
  )
}

const isOssUrl = (baseUrl: string) => {
  return (
    baseUrl.includes(CONFIG.photoPrefix) ||
    baseUrl.includes('net-cloud.xhey.top')
  )
}

const splitUrl = (url: string) => {
  const index = url.indexOf('?')
  if (index === -1) {
    return { base: url, query: '' }
  }
  return { base: url.substring(0, index), query: url.substring(index + 1) }
}

const isInlineUrl = (url: string) =>
  url.startsWith('data:') || url.startsWith('blob:')

const hasAuthQuery = (query: string) => {
  if (!query) return false
  return /(?:^|&)(?:OSSAccessKeyId|Signature|Expires|x-oss-signature|x-oss-security-token|X-Amz-Algorithm|X-Amz-Credential|X-Amz-Signature|X-Amz-Date|X-Amz-Expires|token|sign|signature)=/i.test(
    query
  )
}

const stripProcessParams = (query: string) => {
  if (!query) return ''
  const parts = query.split('&').filter(Boolean)
  const cleaned = parts.filter((part) => {
    if (part.startsWith('x-oss-process=')) return false
    if (part.startsWith('imageView2')) return false
    if (part.startsWith('imageMogr2')) return false
    return true
  })
  return cleaned.join('&')
}

const useImageProcessUrl = () => {
  const buildProcessedUrl = useCallback(
    (url: string, width: number, quality: number = 70) => {
      if (!url) return ''
      if (isInlineUrl(url)) {
        return url
      }
      const { base, query } = splitUrl(url)
      const isTencent = isTencentUrl(base)
      const isOss = isOssUrl(base)
      if (!isTencent && !isOss) {
        return url
      }
      if (hasAuthQuery(query)) {
        return url
      }
      const cleanedQuery = stripProcessParams(query)
      const baseWithQuery = cleanedQuery ? `${base}?${cleanedQuery}` : base
      const separator = cleanedQuery ? '&' : '?'
      if (isTencent) {
        return `${baseWithQuery}${separator}imageView2/2/w/${width}`
      }
      return `${baseWithQuery}${separator}x-oss-process=image/resize,w_${width}/quality,q_${quality}`
    },
    []
  )

  const buildThumbUrl = useCallback(
    (url?: string, thumbWidth: number = 240, quality: number = 70) => {
      if (!url) return ''
      if (isInlineUrl(url)) {
        return url
      }
      const dpr =
        typeof window !== 'undefined'
          ? Math.min(window.devicePixelRatio || 1, MAX_DPR)
          : 1
      const targetWidth = Math.max(
        MIN_THUMB_WIDTH,
        Math.round(thumbWidth * dpr)
      )
      return buildProcessedUrl(url, targetWidth, quality)
    },
    [buildProcessedUrl]
  )

  return {
    buildProcessedUrl,
    buildThumbUrl
  }
}

export default useImageProcessUrl
